/**
 * Role Constants and Types
 * Industry standard: Use role names (strings) everywhere except database relationships
 */

// Role names as constants for consistency
export const ROLE_NAMES = {
  MEMBER: 'member',
  PASTOR: 'pastor',
  MEDIA_TEAM: 'media_team',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
} as const;

// Role IDs for database relationships (internal use only)
export const ROLE_IDS = {
  ADMIN: 1,
  MEMBER: 2,
  MEDIA_TEAM: 3,
  PASTOR: 4,
  MODERATOR: 5,
} as const;

// Type for role names
export type RoleName = typeof ROLE_NAMES[keyof typeof ROLE_NAMES];

// Type for role IDs
export type RoleId = typeof ROLE_IDS[keyof typeof ROLE_IDS];

// Union types for flexibility
export type RoleNameString = RoleName;
export type RoleIdNumber = RoleId;

// Role hierarchy levels (higher number = more permissions)
export const ROLE_LEVELS: Record<string, number> = {
  [ROLE_NAMES.MEMBER]: 1,
  [ROLE_NAMES.PASTOR]: 2,
  [ROLE_NAMES.MEDIA_TEAM]: 3,
  [ROLE_NAMES.MODERATOR]: 4,
  [ROLE_NAMES.ADMIN]: 5,
} as const;

// Permission groups
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  // Content creation
  CAN_CREATE_STREAMS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  CAN_CREATE_CONTENT: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MODERATOR],

  // Moderation
  CAN_MODERATE_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR],
  CAN_MODERATE_USERS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR],
  CAN_MANAGE_STREAMS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],

  // Administration
  CAN_MANAGE_USERS: [ROLE_NAMES.ADMIN],
  CAN_VIEW_ANALYTICS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  CAN_MANAGE_SYSTEM: [ROLE_NAMES.ADMIN],
};

// Comment-specific permissions matrix
export const COMMENT_PERMISSIONS: Record<string, string[]> = {
  // Comment actions by role
  CAN_POST_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER],
  CAN_EDIT_OWN_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER],
  CAN_DELETE_OWN_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER],
  CAN_DELETE_ANY_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR],
  CAN_MODERATE_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR],
  CAN_REPORT_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER],
  CAN_TRANSLATE_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER],
  CAN_VIEW_MODERATION_QUEUE: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR],
  CAN_PIN_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM],
  CAN_HIGHLIGHT_COMMENTS: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM],
};

// Helper to check comment permissions
export function canComment(userRole: string, permission: keyof typeof COMMENT_PERMISSIONS): boolean {
  return COMMENT_PERMISSIONS[permission]?.includes(userRole as RoleName) ?? false;
}

// Helper functions
export function getRoleId(roleName: string): number {
  const entries = Object.entries(ROLE_NAMES) as [keyof typeof ROLE_NAMES, string][];
  const key = entries.find(([, name]) => name === roleName)?.[0];
  if (!key) {
    // Try to find by ID if a number was passed as string
    const id = parseInt(roleName);
    if (!isNaN(id)) return id;
    throw new Error(`Unknown role name: ${roleName}`);
  }
  return ROLE_IDS[key];
}

export function getRoleName(roleId: number): string {
  // First try the mapped roles
  const entries = Object.entries(ROLE_IDS) as [keyof typeof ROLE_IDS, number][];
  const key = entries.find(([, id]) => id === roleId)?.[0];
  if (key) return ROLE_NAMES[key];

  // Fallback for unmapped IDs
  const fallbackRoles: Record<number, string> = {
    1: ROLE_NAMES.ADMIN,
    2: ROLE_NAMES.MEMBER,
    3: ROLE_NAMES.MEDIA_TEAM,
    4: ROLE_NAMES.PASTOR,
    5: ROLE_NAMES.MODERATOR,
  };
  return fallbackRoles[roleId] || ROLE_NAMES.MEMBER;
}

export function hasPermission(userRole: string, permission: keyof typeof ROLE_PERMISSIONS): boolean {
  return ROLE_PERMISSIONS[permission].includes(userRole as RoleName);
}

/**
 * Canonical check for whether a role can access admin surfaces.
 * Keep this centralized so route guards and pages stay in sync.
 */
export function canAccessAdminArea(userRole: string): boolean {
  const adminRoles: RoleName[] = [
    ROLE_NAMES.ADMIN,
    ROLE_NAMES.PASTOR,
    ROLE_NAMES.MEDIA_TEAM,
    ROLE_NAMES.MODERATOR,
  ];
  return adminRoles.includes(userRole as RoleName);
}

export function canManageRole(managerRole: RoleName, targetRole: RoleName): boolean {
  return ROLE_LEVELS[managerRole] > ROLE_LEVELS[targetRole];
}

export function getAllRoleNames(): RoleName[] {
  return Object.values(ROLE_NAMES);
}

export function getAllRoleIds(): RoleId[] {
  return Object.values(ROLE_IDS);
}
